import { useEffect } from 'react'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useFirestore, useFirestoreGeneralOrderBy, useFirestoreOrderBy } from '../../firebase/useFirestore'
import useSettings from '../../hooks/Settings'
import { db, timestamp } from '../../firebase/config'
import { v4 as uuid } from 'uuid'
import { client } from '../../hooks/Client'
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from '../../StateManagment/Auth'
import Location from "../../hooks/Location"
import { useContext, useRef, useState } from "react";
import saveFile from '../../hooks/SaveFile'
import DownloadIcon from '../../images/icons/download-icon.png'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import DatalistOptionsPersonas from '../../components/personas/DataListOptionsPersonas';
import { genderOptions, genderLabels } from '../../helpers/dataHelpers/covariates';
import DataFieldValue from '../../components/common/dataFields/DataFieldValue'
import DataFieldTypeAndOptions from '../../components/common/dataFields/DataFieldTypeAndOptions'
import { resolveDataFieldType } from '../../helpers/dataFields/dataFieldTypes'
import useDataFieldOptions from '../../hooks/useDataFieldOptions'
import uploadDataFieldFile from '../../helpers/dataFields/uploadDataFieldFile'
import Table from '../../components/common/table/Table.jsx';
import TableCell from '../../components/common/table/TableCell.jsx';
import TableDragHandleCell from '../../components/common/table/TableDragHandleCell.jsx';
import Modal from '../../components/common/Modal';
import Tooltip from '../../components/common/Tooltip';
import { DATA_TITLE_TOOLTIP_DELAY } from '../../helpers/tooltip/tooltipDelays';
import UploadProgress from '../../components/common/UploadProgress.jsx';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';
import defaultTargetgroupFromResearch from '../../helpers/personas/defaultTargetgroupFromResearch';
import AgendaColorPicker from '../../components/personas/AgendaColorPicker';
import softDeleteDoc from '../../helpers/softDeleteDoc';
import PersonaTariffs from '../../components/personas/tariffs/PersonaTariffs';

const Data = ({personaNavigation, item, personaDocid}) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // State
    const [file, setFile] = useState(null);
    const [fileMeta, setFileMeta] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [showAddContainer, setShowAddContainer] = useState(false);
    const [dragArrayData, setDragArrayData] = useState([]);
    /** @type {[object|null, Function]} */
    // Alleen de docid, niet het document zelf: het bewerkvenster toont de soort
    // én — afhankelijk daarvan — de antwoordopties, dus het moet de regel volgen
    // zoals hij NU is.
    // AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
    const [editDocid, setEditDocid] = useState('');
    const [dataUploadError, setDataUploadError] = useState('');
    const [dragArrayNetwork, setDragArrayNetwork] = useState([]);
    const [dragArrayRisks, setDragArrayRisks] = useState([]);
    const [dragArrayDocuments, setDragArrayDocuments] = useState([]);
    const [dragArraySpecifics, setDragArraySpecifics] = useState([]);

    // Hooks
    const personaId = Location()[3]
    const fileInputRef = useRef(null);
    const impactFunctionality = useSettings().impactFunctionality

    // Firestore
    const routes = useFirestore('PersonaRoutes')
    const risks = useFirestoreGeneralOrderBy('PersonaRisks', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const networks = useFirestoreGeneralOrderBy('PersonaNetwork', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const data = useFirestoreGeneralOrderBy('PersonaData', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const { optionsFor, optionKey } = useDataFieldOptions()

    /** De regel die op dit moment bewerkt wordt, altijd uit de verse lijst. */
    const editItem = (data || []).find((entry) => entry.docid === editDocid) || null
    const documents = useFirestoreGeneralOrderBy('PersonaDocuments', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const specifics = useFirestoreGeneralOrderBy('PersonaSpecifics', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const userRoles = useFirestore('UserRoles')
    // The org's doelgroepen (TOC target groups) to link this persona to.
    const stakeholders = useFirestoreOrderBy('Stakeholders', 'Position', 'asc')
    // De doelgroep volgt meestal al uit het onderzoek waaraan de medewerker
    // meedoet; die staat dan voorgeselecteerd zolang er niets expliciet is gekozen.
    // AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md
    const researchDocs = useFirestoreOrderBy('Research', 'Position', 'asc')
    const derivedTargetgroup = defaultTargetgroupFromResearch({ linkedIds: item?.Research, researches: researchDocs })

    // Whenever Firestore data changes, update dragArray state
    useEffect(() => {
        setDragArrayData(data);
        setDragArrayNetwork(networks);
        setDragArrayRisks(risks);
        setDragArrayDocuments(documents);
        setDragArraySpecifics(specifics);
    }, [data, networks, risks, documents, specifics]);

    // DRAG & DROP: data
  const handleDragEndData = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayData);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update local state to reflect the reorder immediately
    setDragArrayData(items);

    // Now update Firestore with new positions
    items.forEach((item, idx) => {
      db.collection('PersonaData')
        .doc(item.docid) // assuming .docid is stored for each doc
        .update({ Position: idx + 1 });
    });

    // Show "saved" icon
    setSaved('flex');
  };

  // DRAG & DROP: network
  const handleDragEndNetwork = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayNetwork);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update local state to reflect the reorder immediately
    setDragArrayNetwork(items);

    // Now update Firestore with new positions
    items.forEach((item, idx) => {
      db.collection('PersonaNetwork')
        .doc(item.docid) // assuming .docid is stored for each doc
        .update({ Position: idx + 1 });
    });

    // Show "saved" icon
    setSaved('flex');
  };

    // DRAG & DROP: risks
    const handleDragEndRisks = (result) => {

        if (!result.destination) return;

        const items = Array.from(dragArrayRisks);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        // Update local state to reflect the reorder immediately
        setDragArrayRisks(items);

        // Now update Firestore with new positions
        items.forEach((item, idx) => {
        db.collection('PersonaRisks')
            .doc(item.docid) // assuming .docid is stored for each doc
            .update({ Position: idx + 1 });
        });

        // Show "saved" icon
        setSaved('flex');
    };

    // DRAG & DROP: specifics
    const handleDragEndSpecifics = (result) => {

        if (!result.destination) return;

        const items = Array.from(dragArraySpecifics);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        // Update local state to reflect the reorder immediately
        setDragArraySpecifics(items);

        // Now update Firestore with new positions
        items.forEach((item, idx) => {
        db.collection('PersonaSpecifics')
            .doc(item.docid) // assuming .docid is stored for each doc
            .update({ Position: idx + 1 });
        });

        // Show "saved" icon
        setSaved('flex');
    };

    // DRAG & DROP: documents
    const handleDragEndDocuments = (result) => {

        if (!result.destination) return;

        const items = Array.from(dragArrayDocuments);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        // Update local state to reflect the reorder immediately
        setDragArrayDocuments(items);

        // Now update Firestore with new positions
        items.forEach((item, idx) => {
        db.collection('PersonaDocuments')
            .doc(item.docid) // assuming .docid is stored for each doc
            .update({ Position: idx + 1 });
        });

        // Show "saved" icon
        setSaved('flex');
    };

    // Functions
    const nawOptions = [
        'Voornaam',
        'Achternaam',
        'Straatnaam',
        'Huisnummer',
        'Postcode',
        'Woonplaats',
        'Telefoonnummer',
        'Email',
        'BSN',
        'Geboortedatum',
        'Geslacht',
        'Land van herkomst',
        'Documentnummer',
        'Document type',
        'Document verloopdatum',
        'Toestemming beeldmateriaal',
        'OV-kaartnummer',
        'Centrumzone OV',
        'Kaart geldig tot',
    ]

    const startDateHandler = (e) => {

        const value = e.target.value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            StartDate: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const endDateHandler = (e) => {

        const value = e.target.value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            EndDate: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Function to handle the hours per week
    const HoursPerWeekHandler = (e) => {

        const value = e.target.value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            HoursPerWeek: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const addNetwork = () => {

        db.collection('PersonaNetwork')
        .add({
            ID: uuid(),
            PersonaID: personaId,
            Name: '',
            PhoneNumber: '',
            CompagnyID: client,
            Compagny: client,
            Position: networks.length + 1,
            Timestamp: timestamp
        })

    }

    const addRisk = () => {

        db.collection('PersonaRisks')
        .add({
            ID: uuid(),
            PersonaID: personaId,
            Risk: '',
            Description: '',
            CompagnyID: client,
            Compagny: client,
            Position: risks.length + 1,
            Timestamp: timestamp
        })

    }

    const titleHandler = (e) => {

        const docid = e.target.dataset.docid
        const title = e.target.value

        db.collection('PersonaData')
        .doc(docid)
        .update({
            Title: title
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const valueHandler = (docid, value) => {

        db.collection('PersonaData')
        .doc(docid)
        .update({
            Value: value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Het veldtype bepaalt hoe het gegeven ingevuld wordt. Komt de regel uit een
    // intakeformulier, dan staat het er al op; hier kiest de gebruiker het zelf.
    // AI-CONTEXT: See docs/features/gegevensveld-types.md
    const fieldTypeHandler = (fieldType) => {

        if (!editDocid) return

        db.collection('PersonaData')
        .doc(editDocid)
        .update({ FieldType: fieldType })
        .then(() => {
            setSaved('flex')
        })

    }

    const uploadHandler = async (docid, file) => {

        setDataUploadError('')

        try {
            const url = await uploadDataFieldFile({ file, ownerId: personaId })
            await db.collection('PersonaData').doc(docid).update({ Value: url })
            setSaved('flex')
        } catch (err) {
            // Laten zien in plaats van wegslikken: een mislukte upload die
            // eruitziet als een gelukte kost de gebruiker het document.
            console.error('Uploaden van het gegeven mislukt:', err)
            setDataUploadError('Het bestand kon niet worden geüpload. Probeer het opnieuw.')
        }

    }

    // --- Antwoordopties van een meerkeuzeveld ---
    const addOption = () => {
        if (!editItem) return

        db.collection('MultipleQuestionOptions').add({
            ID: uuid(),
            Option: '',
            Question: editItem.Title || '',
            Field: optionKey(editItem),
            CompagnyID: client,
            Compagny: client,
            Position: optionsFor(editItem).length + 1,
            Timestamp: timestamp,
        }).then(() => setSaved('flex'))
    }

    const changeOption = (docid, value) => {
        db.collection('MultipleQuestionOptions').doc(docid).update({ Option: value })
        .then(() => setSaved('flex'))
    }

    const deleteOption = (docid) => {
        softDeleteDoc({ collection: 'MultipleQuestionOptions', docid })
        .then(() => setSaved('flex'))
    }

    const reorderOptions = (items) => {
        items.forEach((optie, idx) => {
            db.collection('MultipleQuestionOptions').doc(optie.docid).update({ Position: idx + 1 })
        })
        setSaved('flex')
    }

    const deleteNAW = (e) => {

        const docid = e.currentTarget.dataset.docid

        softDeleteDoc({ collection: 'PersonaData', docid, authId: auth?.ID })
        .then(() => {
            setSaved('flex')
        })
    }

    const routeHandler = (e) => {

        const value = e.target.options[e.target.selectedIndex].value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            RouteID: value
        })

    }

    const networkNameHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaNetwork')
        .doc(docid)
        .update({
           Title: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const networkRelationHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaNetwork')
        .doc(docid)
        .update({
            Relation: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const networkPhoneHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaNetwork')
        .doc(docid)
        .update({
            PhoneNumber: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const networkEmailHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaNetwork')
        .doc(docid)
        .update({
            Email: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const riskTitleHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaRisks')
        .doc(docid)
        .update({
            Risk: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const riskValueHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaRisks')
        .doc(docid)
        .update({
            Description: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const deleteNetwork = (e) => {

        const docid = e.currentTarget.dataset.docid

        db.collection('PersonaNetwork')
        .doc(docid)
        .delete()
        .then(() => {
            setSaved('flex')
        })

    }

    const deleteRisk = (e) => {

        const docid = e.currentTarget.dataset.docid

        db.collection('PersonaRisks')
        .doc(docid)
        .delete()
        .then(() => {
            setSaved('flex')
        })

    }

    
    // Function to handle file selection
    const handleFileChange = (e) => {
        const document = e.target.files[0];
        if (document) {
            setUploadError(null);
            saveFile(e, setFile, setFileMeta, setUploadProgress, (err) => setUploadError(err?.message || 'Upload mislukt'));
        }
    };

    // Function to handle file save
    const handleSaveDocument = () => {
        db.collection('PersonaDocuments').add({
            ID: uuid(),
            PersonaID: personaId,
            Title: fileMeta.name,
            Type: '',
            URL: file,
            CompagnyID: client,
            Compagny: client,
            Position: documents.length + 1,
            Timestamp: timestamp
        })
        .then(() => {
            setSaved('flex')
            setShowAddContainer(false)
        })
    };

    const documentTypeHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaDocuments')
        .doc(docid)
        .update({
            Type: e.target.options[e.target.selectedIndex].value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    const deleteDocument = (e) => {

        const docid = e.currentTarget.dataset.docid

        db.collection('PersonaDocuments')
        .doc(docid)
        .delete()
        .then(() => {
            setSaved('flex')
        })

    }

    const documentTitleHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaDocuments')
        .doc(docid)
        .update({
            Title: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    };

    const addData = () => {
            
        db.collection('PersonaData')
        .add({
            ID: uuid(),
            PersonaID: personaId,
            Title: '',
            Value: '',
            FieldType: '',
            CompagnyID: client,
            Compagny: client,
            Position: data.length + 1,
            Timestamp: timestamp
        })
        .then(() => {
            setSaved('flex')
        })
    }

    // Function to add a specific
    const addSpecific = () => {

        db.collection('PersonaSpecifics')
        .add({
            ID: uuid(),
            PersonaID: personaId,
            Specific: '',
            Description: '',
            CompagnyID: client,
            Compagny: client,
            Position: specifics.length + 1,
            Timestamp: timestamp
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Function to handle the specific title
    const specificTitleHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaSpecifics')
        .doc(docid)
        .update({
            Specific: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Function to handle the specific value
    const specificValueHandler = (e) => {

        const docid = e.target.dataset.docid

        db.collection('PersonaSpecifics')
        .doc(docid)
        .update({
            Description: e.target.value
        })
        .then(() => {
            setSaved('flex')
        })

    }

    // Function to delete a specific
    const deleteSpecific = (e) => {

        const docid = e.currentTarget.dataset.docid

        db.collection('PersonaSpecifics')
        .doc(docid)
        .delete()
        .then(() => {
            setSaved('flex')
        })

    }

    const itemRoleHandler = (e) => {
        const value = e.target.options[e.target.selectedIndex].value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            UserRoleID: value
        })
        .then(() => {
            setSaved('flex')
        })
    }

    // Link the persona to one doelgroep (Stakeholders.ID). Single for now (ADR-009).
    const stakeholderHandler = (e) => {
        const value = e.target.value

        db.collection('Personas')
        .doc(personaDocid)
        .update({
            StakeholderID: value
        })
        .then(() => {
            setSaved('flex')
        })
    }

    const dataColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'title', label: 'TITEL', headerClassName: 'common-table__title-column' },
        { key: 'value', label: 'GEGEVEN', searchable: false },
        { key: 'edit', label: 'AANPASSEN', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

    const networkColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'name', label: 'NAAM' },
        { key: 'relation', label: 'RELATIE', searchable: false },
        { key: 'phone', label: 'TELEFOONNUMMER', searchable: false },
        { key: 'email', label: 'EMAIL', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

    const specificColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'specific', label: 'BIJZONDERHEID' },
        { key: 'description', label: 'OMSCHRIJVING', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

    const riskColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'risk', label: 'RISICO' },
        { key: 'description', label: 'OMSCHRIJVING', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

    const documentColumns = [
        { key: 'drag', label: '', searchable: false },
        { key: 'title', label: 'TITEL' },
        { key: 'type', label: 'TYPE', searchable: false },
        { key: 'download', label: 'DOWNLOADEN', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ];

  return (
    <div style={{display: personaNavigation === 'data' ? 'block' : 'none'}}>
        <datalist id="form-field-item-titles">
            <DatalistOptionsPersonas />
      </datalist>
        <ReadOnlyGuard edit="personas.edit">
          {(canEdit) => (
            <div>
            <div className="table-container dashboard-container">
                <h3>Algemeen</h3>
                {dataUploadError && <p className="data-field-error" role="alert">{dataUploadError}</p>}
                <Table
                    columns={dataColumns}
                    items={dragArrayData || []}
                    getItemKey={(entry) => entry.ID}
                    renderRow={(entry) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                {/* Zelfde afspraak als bij de huishoudens: de titel wordt
                                    afgekapt, de tooltip maakt hem alsnog leesbaar, en de
                                    vertraging houdt het rustig.
                                    AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile */}
                                <Tooltip content={entry.Title || ''} placeholder="Nog geen titel" delay={DATA_TITLE_TOOLTIP_DELAY}>
                                    <input
                                        type="text"
                                        defaultValue={entry.Title}
                                        data-docid={entry.docid}
                                        onBlur={titleHandler}
                                        list="form-field-item-titles"
                                        placeholder="Kies of typ een titel"
                                        autoComplete="off"
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <DataFieldValue
                                    item={entry}
                                    canEdit={canEdit}
                                    options={optionsFor(entry)}
                                    onChange={valueHandler}
                                    onUpload={uploadHandler}
                                    genderOptions={genderOptions}
                                    genderLabels={genderLabels}
                                />
                            </TableCell>
                            <TableCell>
                                {canEdit && (
                                    <EditOutlinedIcon
                                        className='table-delete-icon'
                                        onClick={() => setEditDocid(entry.docid)}
                                    />
                                )}
                            </TableCell>
                            <TableCell>
                                {canEdit && <DeleteOutlinedIcon className='table-delete-icon' data-docid={entry.docid} onClick={deleteNAW} />}
                            </TableCell>
                        </>
                    )}
                    searchAccessor={(entry) => `${entry.Title || ''} ${entry.Value || ''}`}
                    searchPlaceholder="Zoeken in gegevens"
                    emptyMessage="Geen gegevens gevonden."
                    draggable
                    droppableId="data"
                    onDragEnd={handleDragEndData}
                    addRow={canEdit ? {
                        toolTipContent: 'Gegeven toevoegen',
                        onClick: addData,
                    } : undefined}
                />
            </div>

            {/* Role */}
             <div className="table-container dashboard-container">
                <h3>Rol</h3>
                {/* Gestuurd, net als de doelgroep- en trajectmenu's hieronder: de rollen
                    komen uit een eigen Firestore-read en kunnen ná de medewerker binnen
                    zijn. `defaultValue` wordt alleen bij het aanhaken gelezen, dus dan
                    valt het menu terug op '-- Selecteer een rol --' en blijft daar staan.
                    AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                <select name="" id="" data-docid={item.docid} onChange={itemRoleHandler} value={item.UserRoleID || ''}>
                    <option value="">-- Selecteer een rol --</option>
                    {userRoles && userRoles.map(role => (
                        <option key={role.docid} value={role.ID}>{role.Name}</option>
                    ))}
                </select>
             </div>

            {/* Eigen kleur in de agenda — afspraken waarop alléén deze persona
                staat. Roosterblokken niet: die zijn altijd grijs. */}
             <div className="table-container dashboard-container">
                <h3>Agenda</h3>
                <AgendaColorPicker docid={item.docid} value={item.AgendaColor} canEdit={canEdit} />
             </div>

            {/* Doelgroep — only when ImpactMeten module is on */}
            {impactFunctionality &&
             <div className="table-container dashboard-container">
                <h3>Doelgroep</h3>
                <select name="" id="" data-docid={item.docid} value={item.StakeholderID || derivedTargetgroup?.stakeholderId || ''} onChange={stakeholderHandler}>
                    <option value="">-- Selecteer een doelgroep --</option>
                    {stakeholders && stakeholders.map(stakeholder => (
                        <option key={stakeholder.ID} value={stakeholder.ID}>{stakeholder.Organisation}</option>
                    ))}
                </select>
                {!item.StakeholderID && derivedTargetgroup && (
                    <p className="measurement-signal-note">
                        Via het onderzoek '{derivedTargetgroup.researchTitle}'. Kies een andere doelgroep om hiervan af te wijken.
                    </p>
                )}
             </div>
            }

            {/* id: doel van de link 'Traject kiezen' op de pagina Facturatie (skipFix). */}
            <div className="table-container dashboard-container" id="persona-traject">
                <h3>Traject</h3>
                <p>Trajectnaam</p>
                <select name="" id="" onChange={routeHandler} value={item.RouteID}>
                    <option value="">-- Selecteer een traject --</option>
                    {routes && routes.map(route => (
                        <option key={route.ID} value={route.ID}>{route.Title}</option>
                    ))}
                </select>
                <p>Startdatum</p>
                <input type="date" defaultValue={item.StartDate} onChange={startDateHandler} />
                <p>Einddatum</p>
                <input type="date" defaultValue={item.EndDate} onChange={endDateHandler}/>
                <p>Uren per week</p>
                <input type="number" defaultValue={item.HoursPerWeek} onChange={HoursPerWeekHandler}/>
            </div>

            <div className="table-container dashboard-container">
                <h3>Netwerk</h3>
                <Table
                    columns={networkColumns}
                    items={dragArrayNetwork || []}
                    getItemKey={(entry) => entry.ID}
                    renderRow={(entry) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Title} onChange={networkNameHandler}/>
                            </TableCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Relation} onChange={networkRelationHandler}/>
                            </TableCell>
                            <TableCell>
                                <input type="phone" data-docid={entry.docid} defaultValue={entry.PhoneNumber} onChange={networkPhoneHandler}/>
                            </TableCell>
                            <TableCell>
                                <input type="email" data-docid={entry.docid} defaultValue={entry.Email} onChange={networkEmailHandler}/>
                            </TableCell>
                            <TableCell>
                                {canEdit && <DeleteOutlinedIcon className='table-delete-icon' data-docid={entry.docid} onClick={deleteNetwork} />}
                            </TableCell>
                        </>
                    )}
                    searchAccessor={(entry) => `${entry.Title || ''} ${entry.Relation || ''} ${entry.PhoneNumber || ''} ${entry.Email || ''}`}
                    searchPlaceholder="Zoeken in netwerk"
                    emptyMessage="Geen netwerkitems gevonden."
                    draggable
                    droppableId="network"
                    onDragEnd={handleDragEndNetwork}
                    addRow={canEdit ? {
                        toolTipContent: 'Netwerk toevoegen',
                        onClick: addNetwork,
                    } : undefined}
                />
            </div>

            <div className="table-container dashboard-container">
                <h3>Aandachtspunten</h3>
                <Table
                    columns={specificColumns}
                    items={dragArraySpecifics || []}
                    getItemKey={(entry) => entry.ID}
                    renderRow={(entry) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Specific} onChange={specificTitleHandler}/>
                            </TableCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Description} onChange={specificValueHandler}/>
                            </TableCell>
                            <TableCell>
                                {canEdit && <DeleteOutlinedIcon className='table-delete-icon' data-docid={entry.docid} onClick={deleteSpecific} />}
                            </TableCell>
                        </>
                    )}
                    searchAccessor={(entry) => `${entry.Specific || ''} ${entry.Description || ''}`}
                    searchPlaceholder="Zoeken in aandachtspunten"
                    emptyMessage="Geen aandachtspunten gevonden."
                    draggable
                    droppableId="specific"
                    onDragEnd={handleDragEndSpecifics}
                    addRow={canEdit ? {
                        toolTipContent: 'BIjzonderheid toevoegen',
                        onClick: addSpecific,
                    } : undefined}
                />
            </div>

            <div className="table-container dashboard-container">
                <h3>Risico's</h3>
                <Table
                    columns={riskColumns}
                    items={dragArrayRisks || []}
                    getItemKey={(entry) => entry.ID}
                    renderRow={(entry) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Risk} onChange={riskTitleHandler}/>
                            </TableCell>
                            <TableCell>
                                <input type="text" data-docid={entry.docid} defaultValue={entry.Description} onChange={riskValueHandler}/>
                            </TableCell>
                            <TableCell>
                                {canEdit && <DeleteOutlinedIcon className='table-delete-icon' data-docid={entry.docid} onClick={deleteRisk} />}
                            </TableCell>
                        </>
                    )}
                    searchAccessor={(entry) => `${entry.Risk || ''} ${entry.Description || ''}`}
                    searchPlaceholder="Zoeken in risico's"
                    emptyMessage="Geen risico's gevonden."
                    draggable
                    droppableId="risks"
                    onDragEnd={handleDragEndRisks}
                    addRow={canEdit ? {
                        toolTipContent: 'Risico toevoegen',
                        onClick: addRisk,
                    } : undefined}
                />
            </div>

            <div className="table-container dashboard-container">
            <h3>Documenten</h3>
            <Table
                columns={documentColumns}
                items={dragArrayDocuments || []}
                getItemKey={(entry) => entry.ID}
                renderRow={(entry) => (
                    <>
                        <TableDragHandleCell>
                            <DragIndicatorOutlinedIcon />
                        </TableDragHandleCell>
                        <TableCell>
                            <input type="text" defaultValue={entry.Title} data-docid={entry.docid} onChange={documentTitleHandler}/>
                        </TableCell>
                        <TableCell>
                            <select name="" id="" defaultValue={entry.Type} data-docid={entry.docid} onChange={documentTypeHandler}>
                                <option value="">-- Selecteer --</option>
                                <option value="care-agreement">Zorgovereenkomst</option>
                                <option value="id">Identiteitsdocument</option>
                                <option value="contract">Contract</option>
                                <option value="vog">VOG</option>
                                <option value="avg">AVG</option>
                                <option value="safety">Veiligheidsplan</option>
                            </select>
                        </TableCell>
                        <TableCell>
                            <a href={entry.URL} target='_blank' rel="noreferrer" download>
                                <img className='table-delete-icon' src={DownloadIcon} alt="Download" />
                            </a>
                        </TableCell>
                        <TableCell>
                            {canEdit && <DeleteOutlinedIcon className='table-delete-icon' data-docid={entry.docid} onClick={deleteDocument} />}
                        </TableCell>
                    </>
                )}
                searchAccessor={(entry) => `${entry.Title || ''} ${entry.Type || ''}`}
                searchPlaceholder="Zoeken in documenten"
                emptyMessage="Geen documenten gevonden."
                draggable
                droppableId="documents"
                onDragEnd={handleDragEndDocuments}
                addRow={canEdit ? {
                    toolTipContent: 'Document toevoegen',
                    onClick: () => setShowAddContainer(true),
                } : undefined}
            />
                <Modal
                    modalOpen={showAddContainer}
                    setModalOpen={setShowAddContainer}
                    pageHeader={'Document toevoegen'}
                    confirm={handleSaveDocument}
                    confirmLabel={'Uploaden'}
                    content={
                        <>
                            <input
                                id='persona-document-upload-input'
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={uploadProgress > 0 && uploadProgress < 100}
                            />
                            <UploadProgress
                                progress={uploadProgress}
                                error={uploadError}
                                fileName={fileMeta?.name}
                            />
                        </>
                    }
                />
        </div>


            </div>
          )}
        </ReadOnlyGuard>
        {/* Eigen recht ('billing.view'/'billing.edit'), dus buiten het slot van
            personas.edit. Leest alleen als dit tabblad openstaat. */}
        <PersonaTariffs personaId={personaId} active={personaNavigation === 'data'} />
        <Modal
            modalOpen={!!editItem}
            setModalOpen={() => setEditDocid('')}
            pageHeader={`Soort en antwoordopties${editItem?.Title ? `: ${editItem.Title}` : ''}`}
            hideConfirm
            cancelLabel="Sluiten"
            content={
                <DataFieldTypeAndOptions
                    type={resolveDataFieldType(editItem)}
                    setType={fieldTypeHandler}
                    dataOptions={optionsFor(editItem)}
                    addOption={addOption}
                    onChangeOption={changeOption}
                    onDeleteOption={deleteOption}
                    onReorderOptions={reorderOptions}
                />
            }
        />
    </div>
  )
}

export default Data
